import { languages, setLanguage, t } from "@excalidraw/excalidraw/i18n";
import { useCallback, useEffect, useState } from "react";

import { getPreferredLanguage } from "../app-language/language-detector";
import { libraryApi, type AuthUser } from "../data/libraryApi";
import {
  cacheAuthUser,
  clearCachedAuthUser,
  getCachedAuthUser,
} from "../data/offlineStore";

import { AuthContext } from "./AuthContext";

import "./auth.scss";

// On a shared browser, don't carry one user's "last open canvas" into another's
// session. (The backend already 404s a foreign id, but this avoids the flash.)
const ACTIVE_CANVAS_KEY = "excalidraw-active-canvas-id";
const LAST_USER_KEY = "pw-last-user-id";

const syncUserStorage = (userId: string) => {
  try {
    if (localStorage.getItem(LAST_USER_KEY) !== userId) {
      localStorage.removeItem(ACTIVE_CANVAS_KEY);
      localStorage.setItem(LAST_USER_KEY, userId);
    }
  } catch {
    /* ignore storage errors */
  }
};

type Phase =
  | { kind: "loading" }
  | { kind: "setup" }
  | { kind: "login" }
  | { kind: "authed"; user: AuthUser };

const AuthForm = ({
  mode,
  onAuthed,
}: {
  mode: "setup" | "login";
  onAuthed: (user: AuthUser) => void;
}) => {
  const isSetup = mode === "setup";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError(t("auth.fillAllFields"));
      return;
    }
    if (isSetup) {
      if (password.length < 6) {
        setError(t("auth.passwordTooShort"));
        return;
      }
      if (password !== confirm) {
        setError(t("auth.passwordsDontMatch"));
        return;
      }
    }
    setBusy(true);
    try {
      const res = isSetup
        ? await libraryApi.setup({ username: username.trim(), password })
        : await libraryApi.login({ username: username.trim(), password });
      onAuthed(res.user);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("401")) {
        setError(t("auth.invalidCredentials"));
      } else if (msg.includes("429")) {
        setError(t("auth.tooManyAttempts"));
      } else if (msg.includes("409")) {
        setError(t("auth.usernameTaken"));
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pw-auth">
      <form className="pw-auth__card" onSubmit={submit}>
        <div className="pw-auth__brand">PizzarWeb</div>
        <h1 className="pw-auth__title">
          {isSetup ? t("auth.setupTitle") : t("auth.loginTitle")}
        </h1>
        {isSetup && <p className="pw-auth__hint">{t("auth.setupHint")}</p>}

        <label className="pw-auth__field">
          <span>{t("auth.username")}</span>
          <input
            autoFocus
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>
        <label className="pw-auth__field">
          <span>{t("auth.password")}</span>
          <input
            type="password"
            autoComplete={isSetup ? "new-password" : "current-password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        {isSetup && (
          <label className="pw-auth__field">
            <span>{t("auth.confirmPassword")}</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </label>
        )}

        {error && <div className="pw-auth__error">{error}</div>}

        <button className="pw-auth__submit" type="submit" disabled={busy}>
          {busy ? "…" : isSetup ? t("auth.createAdmin") : t("auth.signIn")}
        </button>
      </form>
    </div>
  );
};

export const AuthGate = ({ children }: { children: React.ReactNode }) => {
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  // The login/setup screens render before the app initializes i18n, so set the
  // user's preferred language here (otherwise t() falls back to English).
  const [langLoaded, setLangLoaded] = useState(false);

  useEffect(() => {
    const lang = languages.find((l) => l.code === getPreferredLanguage());
    Promise.resolve(lang ? setLanguage(lang) : undefined).finally(() =>
      setLangLoaded(true),
    );
  }, []);

  const load = useCallback(async () => {
    try {
      const status = await libraryApi.authStatus();
      if (status.needsSetup) {
        setPhase({ kind: "setup" });
      } else if (status.authenticated && status.user) {
        cacheAuthUser(status.user);
        syncUserStorage(status.user.id);
        setPhase({ kind: "authed", user: status.user });
      } else {
        setPhase({ kind: "login" });
      }
    } catch {
      // Backend unreachable (offline). If this user logged in before, let them
      // straight into the app to keep working offline; otherwise show login.
      const cached = getCachedAuthUser();
      if (cached) {
        syncUserStorage(cached.id);
        setPhase({ kind: "authed", user: cached });
      } else {
        setPhase({ kind: "login" });
      }
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onAuthed = useCallback((user: AuthUser) => {
    cacheAuthUser(user);
    syncUserStorage(user.id);
    setPhase({ kind: "authed", user });
  }, []);

  const logout = useCallback(async () => {
    try {
      await libraryApi.logout();
    } catch {
      /* ignore */
    }
    clearCachedAuthUser();
    try {
      localStorage.removeItem(ACTIVE_CANVAS_KEY);
    } catch {
      /* ignore */
    }
    setPhase({ kind: "login" });
  }, []);

  if (!langLoaded || phase.kind === "loading") {
    return <div className="pw-auth pw-auth--loading">{t("auth.loading")}</div>;
  }
  if (phase.kind === "setup" || phase.kind === "login") {
    return <AuthForm mode={phase.kind} onAuthed={onAuthed} />;
  }
  return (
    <AuthContext.Provider value={{ user: phase.user, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
